import React, { useCallback, useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useWhitelabel } from "../../../../contexts/WhitelabelContext";
import NotyfContext from "../../../../contexts/NotyfContext";
import * as Yup from "yup";
import useWindowSize from "../../../../hooks/useWindowSize";
import { Button, Form, Modal, Offcanvas, Table } from "react-bootstrap";
import { Formik } from "formik";
import { validateSimsForAction } from "../../../../redux/slices/sims";
import { blockSuspension } from "../../../../redux/slices/watchdogs";
import useDeviceType from "../../../../hooks/useDeviceType";

const BlockSuspensionModal = ({
  openBlockSuspensionModal,
  setOpenBlockSuspensionModal,
  selectedSims,
  clearSelectedSims,
}) => {
  const handleClose = () => setOpenBlockSuspensionModal(false);
  const dispatch = useDispatch();
  const { backgroundColor, fontColor } = useWhitelabel();
  const notyf = useContext(NotyfContext);
  const [currentStep, setCurrentStep] = useState(1);
  const [width] = useWindowSize();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";
  const [internalShow, setInternalShow] = useState(openBlockSuspensionModal);
  const [fadeOut, setFadeOut] = useState(false);
  const [fileInputKey, setFileInputKey] = React.useState(Date.now());
  const handleToggleModal = useCallback(() => {
    setOpenBlockSuspensionModal(false);
  }, [setOpenBlockSuspensionModal]);

  useEffect(() => {
    if (openBlockSuspensionModal && !isDesktop) {
      setFadeOut(false);
      setInternalShow(true);
    }
  }, [openBlockSuspensionModal, isDesktop]);

  const handleHide = () => {
    setFadeOut(true);
    setTimeout(() => {
      setInternalShow(false);
      handleToggleModal(false);
    }, 300);
  };

  useEffect(() => {
    if (openBlockSuspensionModal) {
      setCurrentStep(selectedSims?.length ? 2 : 1);
      setFileInputKey(Date.now());
    }
  }, [openBlockSuspensionModal, selectedSims]);

  const summarySims = (uploadedSims, selectedSims) =>
    selectedSims?.length
      ? selectedSims.map((s) => ({
          id: s.id,
          imsi: s.imsi || "",
          iccid: s.iccid || "",
        }))
      : uploadedSims;

  const renderSummaryTable = (rows, fields) => (
    <Table hover responsive>
      <thead>
        <tr>
          <th>IMSI</th>
          <th>ICCID</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={`${r.id}-${r.imsi}-${r.iccid}`}>
            <td>{r.imsi}</td>
            <td>{r.iccid}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

  const initialValues = {
    sims: selectedSims.length > 0 ? selectedSims : [],
    file: null,
  };

  const validation = Yup.object().shape({
    sims: Yup.array().required("Required"),
    file: Yup.mixed().when("$requireFile", {
      is: true,
      then: Yup.mixed()
        .required("File is required")
        .test("fileType", "Only CSV or XLSX files are accepted", (value) => {
          if (!value) return false;
          const allowedTypes = [
            "text/csv",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "application/vnd.ms-excel",
          ];
          return allowedTypes.includes(value.type);
        }),
      otherwise: Yup.mixed().nullable().notRequired(),
    }),
  });

  if (isDesktop) {
    return (
      <Modal
        show={openBlockSuspensionModal}
        onHide={() => handleToggleModal()}
        centered
        size="lg"
        backdrop="static"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {currentStep === 1
              ? "Step 1 of 2: Select SIMs (CSV/XLSX)"
              : "Step 2 of 2: Summary & Confirm"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validation}
            context={{ requireFile: selectedSims.length === 0 }}
            onSubmit={async (values, formikHelpers) => {
              if (currentStep === 1) {
                if (!values.sims || values.sims.length === 0) {
                  formikHelpers.setFieldError(
                    "file",
                    "Please upload a CSV/XLSX with at least one IMSI/ICCID."
                  );
                  return;
                }
                setCurrentStep(2);
                return;
              }
              if (currentStep === 2) {
                const data = {
                  simIds: values.sims.map((s) => s.id),
                };
                await blockSuspension({ data })
                  .then(() => {
                    notyf.open({
                      type: "success",
                      message: `Suspension has been prevented for ${
                        values.sims.length
                      } SIM${
                        values.sims.length > 1 ? "s" : ""
                      } in the current billing period.`,
                      duration: 5000,
                      ripple: true,
                      dismissible: false,
                      position: {
                        x: "center",
                        y: "top",
                      },
                    });
                    if (clearSelectedSims) clearSelectedSims();
                  })
                  .catch((error) => {
                    let message = "Error blocking suspension";
                    notyf.open({
                      type: "danger",
                      message: message,
                      duration: 5000,
                      ripple: true,
                      dismissible: false,
                      position: { x: "center", y: "top" },
                    });
                  })
                  .finally(() => {
                    handleToggleModal(false);
                  });
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              validateForm,
              setFieldTouched,
              status,
              setValues,
              setFieldValue,
            }) => {
              return (
                <Form onSubmit={handleSubmit}>
                  {currentStep === 1 && (
                    <React.Fragment>
                      <p className="mb-2">
                        No SIMs are selected. Upload a CSV/XLSX file with a
                        header column named <code>IMSI</code> or{" "}
                        <code>ICCID</code>.
                      </p>
                      <Form.Group controlId="csvUpload" className="mb-3">
                        <Form.Label>Choose CSV/XLSX</Form.Label>
                        <Form.Control
                          key={fileInputKey}
                          type="file"
                          accept=".csv, .xlsx"
                          onChange={async (e) => {
                            const file = e.currentTarget.files?.[0] || null;
                            setFieldValue("file", file);
                            setFieldValue("sims", []);
                            if (!file) return;

                            await validateSimsForAction({
                              file,
                              action: "blockSuspension",
                            })
                              .then((response) => {
                                // 🆕 Expect backend to return [{id, imsi, iccid}, ...]
                                notyf.open({
                                  type: "success",
                                  message: "Sim file validated successfully",
                                  duration: 5000,
                                  ripple: true,
                                  dismissible: false,
                                  position: { x: "center", y: "top" },
                                });
                                setFieldValue("file", null);
                                setFieldValue("sims", response); // keep full objects
                                // Optionally auto-advance to Step 2:
                                setCurrentStep(2);
                              })
                              .catch((error) => {
                                notyf.open({
                                  type: "danger",
                                  message: error,
                                  duration: 5000,
                                  ripple: true,
                                  dismissible: false,
                                  position: { x: "center", y: "top" },
                                });
                                setFieldValue("file", null);
                                setFieldValue("sims", []);
                                setFileInputKey(Date.now());
                              });
                          }}
                          isInvalid={touched.file && !!errors.file}
                        />
                        {touched.file && errors.file && (
                          <div className="text-danger mt-2" role="alert">
                            {errors.file}
                          </div>
                        )}
                        {values.sims.length > 0 && (
                          <div className="mt-2">
                            <strong>{values.sims.length}</strong> SIM
                            {values.sims.length === 1 ? "" : "s"} loaded.
                          </div>
                        )}
                      </Form.Group>
                      <Modal.Footer>
                        <Button
                          variant="primary"
                          style={{
                            "--dynamic-bg-color": backgroundColor,
                            "--dynamic-font-color": fontColor,
                            border: "none",
                          }}
                          onClick={() => {
                            setFieldValue("file", null);
                            setFieldValue("sims", []);
                            handleToggleModal(false);
                          }}
                        >
                          Cancel
                        </Button>

                        <Button
                          variant="primary"
                          style={{
                            "--dynamic-bg-color": backgroundColor,
                            "--dynamic-font-color": fontColor,
                            border: "none",
                          }}
                          onClick={() => setCurrentStep(2)}
                        >
                          Continue
                        </Button>
                      </Modal.Footer>
                    </React.Fragment>
                  )}
                  {currentStep === 2 && (
                    <React.Fragment>
                      <div
                        style={{
                          marginBottom: "1rem",
                          padding: "0.75rem 1rem",
                          backgroundColor: backgroundColor,
                          borderRadius: "0.5rem",
                          fontSize: "0.9rem",
                          color: fontColor,
                        }}
                      >
                        ⚠️ This configuration is{" "}
                        <strong
                          style={{
                            color: fontColor,
                          }}
                        >
                          temporary
                        </strong>{" "}
                        and applies only for the current billing period. With
                        the start of a new billing period, it will be reset to
                        the default functionality.
                      </div>
                      {renderSummaryTable(
                        summarySims(values.sims, selectedSims),
                        values.values
                      )}
                      <Modal.Footer>
                        <Button
                          variant="primary"
                          onClick={() => setCurrentStep(1)}
                          style={{
                            "--dynamic-bg-color": backgroundColor,
                            "--dynamic-font-color": fontColor,
                            border: "none",
                          }}
                        >
                          Back
                        </Button>

                        <Button
                          type="submit"
                          variant="primary"
                          style={{
                            "--dynamic-bg-color": backgroundColor,
                            "--dynamic-font-color": fontColor,
                            border: "none",
                          }}
                        >
                          Confirm & Apply
                        </Button>
                      </Modal.Footer>
                    </React.Fragment>
                  )}
                </Form>
              );
            }}
          </Formik>
        </Modal.Body>
      </Modal>
    );
  } else {
    return (
      <Offcanvas
        show={internalShow}
        placement="bottom"
        scroll
        onHide={handleHide}
        className={`fullscreen-offcanvas ${fadeOut ? "fade-out" : ""}`}
      >
        <Offcanvas.Header closeButton className="fullscreen-offcanvas-header">
          <Offcanvas.Title>
            {currentStep === 1
              ? "Step 1 of 2: Select SIMs (CSV/XLSX)"
              : "Step 2 of 2: Summary & Confirm"}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validation}
            context={{ requireFile: selectedSims.length === 0 }}
            onSubmit={async (values, formikHelpers) => {
              if (currentStep === 1) {
                if (!values.sims || values.sims.length === 0) {
                  formikHelpers.setFieldError(
                    "file",
                    "Please upload a CSV/XLSX with at least one IMSI/ICCID."
                  );
                  return;
                }
                setCurrentStep(2);
                return;
              }
              if (currentStep === 2) {
                const data = {
                  simIds: values.sims.map((s) => s.id),
                };
                await blockSuspension({ data })
                  .then(() => {
                    notyf.open({
                      type: "success",
                      message: `Suspension has been prevented for ${
                        values.sims.length
                      } SIM${
                        values.sims.length > 1 ? "s" : ""
                      } in the current billing period.`,
                      duration: 5000,
                      ripple: true,
                      dismissible: false,
                      position: {
                        x: "center",
                        y: "top",
                      },
                    });
                    if (clearSelectedSims) clearSelectedSims();
                  })
                  .catch((error) => {
                    notyf.open({
                      type: "danger",
                      message: "Error blocking suspension",
                      duration: 5000,
                      ripple: true,
                      dismissible: false,
                      position: { x: "center", y: "top" },
                    });
                  })
                  .finally(() => {
                    handleToggleModal(false);
                  });
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              validateForm,
              setFieldTouched,
              status,
              setValues,
              setFieldValue,
            }) => {
              return (
                <Form onSubmit={handleSubmit}>
                  {currentStep === 1 && (
                    <React.Fragment>
                      <p className="mb-2">
                        No SIMs are selected. Upload a CSV/XLSX file with a
                        header column named <code>IMSI</code> or{" "}
                        <code>ICCID</code>.
                      </p>
                      <Form.Group controlId="csvUpload" className="mb-3">
                        <Form.Label>Choose CSV/XLSX</Form.Label>
                        <Form.Control
                          key={fileInputKey}
                          type="file"
                          accept=".csv, .xlsx"
                          onChange={async (e) => {
                            const file = e.currentTarget.files?.[0] || null;
                            setFieldValue("file", file);
                            setFieldValue("sims", []);
                            if (!file) return;

                            await validateSimsForAction({
                              file,
                              action: "blockSuspension",
                            })
                              .then((response) => {
                                // 🆕 Expect backend to return [{id, imsi, iccid}, ...]
                                notyf.open({
                                  type: "success",
                                  message: "Sim file validated successfully",
                                  duration: 5000,
                                  ripple: true,
                                  dismissible: false,
                                  position: { x: "center", y: "top" },
                                });
                                setFieldValue("file", null);
                                setFieldValue("sims", response); // keep full objects
                                // Optionally auto-advance to Step 2:
                                setCurrentStep(2);
                              })
                              .catch((error) => {
                                notyf.open({
                                  type: "danger",
                                  message: error,
                                  duration: 5000,
                                  ripple: true,
                                  dismissible: false,
                                  position: { x: "center", y: "top" },
                                });
                                setFieldValue("file", null);
                                setFieldValue("sims", []);
                                setFileInputKey(Date.now());
                              });
                          }}
                          isInvalid={touched.file && !!errors.file}
                        />
                        {touched.file && errors.file && (
                          <div className="text-danger mt-2" role="alert">
                            {errors.file}
                          </div>
                        )}
                        {values.sims.length > 0 && (
                          <div className="mt-2">
                            <strong>{values.sims.length}</strong> SIM
                            {values.sims.length === 1 ? "" : "s"} loaded.
                          </div>
                        )}
                      </Form.Group>
                      <React.Fragment>
                        <Button
                          variant="primary"
                          style={{
                            "--dynamic-bg-color": backgroundColor,
                            "--dynamic-font-color": fontColor,
                            border: "none",
                            padding: "10px",
                            width: "100%",
                          }}
                          className="mb-2"
                          onClick={() => setCurrentStep(2)}
                        >
                          Continue
                        </Button>
                        <Button
                          variant="primary"
                          style={{
                            "--dynamic-bg-color": backgroundColor,
                            "--dynamic-font-color": fontColor,
                            border: "none",
                            padding: "10px",
                            width: "100%",
                          }}
                          onClick={() => {
                            setFieldValue("file", null);
                            setFieldValue("sims", []);
                            handleToggleModal(false);
                          }}
                        >
                          Cancel
                        </Button>
                      </React.Fragment>
                    </React.Fragment>
                  )}
                  {currentStep === 2 && (
                    <React.Fragment>
                      <div
                        style={{
                          marginBottom: "1rem",
                          padding: "0.75rem 1rem",
                          backgroundColor: backgroundColor,
                          borderRadius: "0.5rem",
                          fontSize: "0.9rem",
                          color: fontColor,
                        }}
                      >
                        ⚠️ This configuration is{" "}
                        <strong
                          style={{
                            color: fontColor,
                          }}
                        >
                          temporary
                        </strong>{" "}
                        and applies only for the current billing period. With
                        the start of a new billing period, it will be reset to
                        the default functionality.
                      </div>
                      {renderSummaryTable(
                        summarySims(values.sims, selectedSims),
                        values.values
                      )}
                      <React.Fragment>
                        <Button
                          variant="primary"
                          style={{
                            "--dynamic-bg-color": backgroundColor,
                            "--dynamic-font-color": fontColor,
                            border: "none",
                            padding: "10px",
                            width: "100%",
                          }}
                          className="mb-2"
                          onClick={handleSubmit}
                        >
                          Confirm & Apply
                        </Button>
                        <Button
                          variant="primary"
                          onClick={() => setCurrentStep(1)}
                          style={{
                            "--dynamic-bg-color": backgroundColor,
                            "--dynamic-font-color": fontColor,
                            border: "none",
                            padding: "10px",
                            width: "100%",
                          }}
                        >
                          Back
                        </Button>
                      </React.Fragment>
                    </React.Fragment>
                  )}
                </Form>
              );
            }}
          </Formik>
        </Offcanvas.Body>
      </Offcanvas>
    );
  }
};

export default BlockSuspensionModal;
